import { DndContext, DragOverlay, closestCenter, pointerWithin, PointerSensor, useSensor, useSensors, type DragStartEvent, type DragEndEvent, type Modifier } from '@dnd-kit/core';
import { useState, useCallback, type ReactNode } from 'react';

export interface DragPayload {
  sourceArea: 'widget' | 'history' | 'folder';
  itemIds: string[];   // supports multi-drag
  dataIds: string[];   // corresponding DataItem ids
  folderIds?: string[];
}

interface DndProviderProps {
  children: ReactNode;
  onDragEnd?: (payload: DragPayload, targetArea: string, targetFolderId?: string) => void;
}

/**
 * App-level DnD context provider.
 * Wraps the main layout to enable cross-area drag & drop.
 */
const DndProvider = ({ children, onDragEnd: onDragEndProp }: DndProviderProps) => {
  const [activePayload, setActivePayload] = useState<DragPayload | null>(null);

  // Require 8px movement before activating drag — lets clicks pass through
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  /**
   * Custom modifier to position the top-left of the overlay right next to the cursor.
   * This overrides the default offset relative to the original element's pickup point.
   */
  const followCursorModifier: Modifier = ({ transform, activatorEvent, active }) => {
    if (activatorEvent && active?.rect.current?.initial) {
      const initialRect = active.rect.current.initial;
      const mouseEvent = activatorEvent as MouseEvent;
      
      // Calculate where the mouse is relative to the original element's top-left at the start of drag
      const clickOffsetX = mouseEvent.clientX - initialRect.left;
      const clickOffsetY = mouseEvent.clientY - initialRect.top;
      
      return {
        ...transform,
        x: transform.x + clickOffsetX + 12, // 12px offset to the right
        y: transform.y + clickOffsetY - 10, // Slight upward shift for better visibility
      };
    }
    return transform;
  };

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const payload = event.active.data.current as DragPayload | undefined;
    if (payload) {
      setActivePayload(payload);
    }
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { over } = event;
    if (activePayload && over) {
      const targetArea = over.data.current?.area as string | undefined;
      const targetFolderId = over.data.current?.folderId as string | undefined;
      if (targetArea) {
        onDragEndProp?.(activePayload, targetArea, targetFolderId);
      }
    }
    setActivePayload(null);
  }, [activePayload, onDragEndProp]);

  const handleDragCancel = useCallback(() => {
    setActivePayload(null);
  }, []);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      autoScroll={{
        threshold: { x: 0, y: 0.1 },
        acceleration: 10,
        canScroll: (element) => {
          const style = window.getComputedStyle(element);
          return (
            (element.scrollHeight > element.clientHeight) &&
            (style.overflowY === 'auto' || style.overflowY === 'scroll')
          );
        },
      }}
    >
      {children}
      <DragOverlay dropAnimation={null} modifiers={[followCursorModifier]}>
        {activePayload && (
          <div className="glass-panel-static rounded-lg px-3 py-2 text-sm text-foreground shadow-lg opacity-90 pointer-events-none w-fit whitespace-nowrap">
            {activePayload.itemIds.length}개 항목
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};

export default DndProvider;
export { DndProvider };
